import React from 'react'
import {Box, BoxProps, Link, StackProps, VStack} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGlobalConfigState} from '_context'
import {useTencentCaptcha} from '_customHooks'

interface FormPrivacyDisclaimerProps {
    /** Style props for the privacy policy line (spread onto its Box). */
    privacyMessageProps?: BoxProps
    /** Style props for the Google reCAPTCHA disclaimer line (spread onto its Box). */
    recaptchaMessageProps?: BoxProps
    /** Style props for the wrapper that stacks the two lines (spacing, alignment). Overrides the defaults. */
    containerProps?: StackProps
}

/**
 * @name FormPrivacyDisclaimer
 *
 * @component
 *
 * @description
 * Renders the standard privacy disclaimer shown at the bottom of site forms:
 * 1. A privacy policy line linking to the SFRA `/privacypolicy` page.
 * 2. The Google reCAPTCHA disclaimer, rendered only when reCAPTCHA is active for the site.
 *
 * The Google reCAPTCHA disclaimer is shown only when Google reCAPTCHA is active for the site
 * (`apikeyv3_recaptcha`) and Tencent Captcha is not enabled — both are site-level preferences.
 * The public locale segment (`locale.alias`) is also resolved internally, so callers only
 * provide styling.
 *
 * @param {FormPrivacyDisclaimerProps} props - The properties object.
 * @param {BoxProps} [props.privacyMessageProps] - Style props for the privacy policy line.
 * @param {BoxProps} [props.recaptchaMessageProps] - Style props for the reCAPTCHA disclaimer line.
 *
 * @returns {JSX.Element} Rendered `FormPrivacyDisclaimer` component.
 */
const FormPrivacyDisclaimer: React.FC<FormPrivacyDisclaimerProps> = ({
    privacyMessageProps,
    recaptchaMessageProps,
    containerProps,
}: FormPrivacyDisclaimerProps): JSX.Element => {
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {appPreferences} = useGlobalConfigState()
    const {isTencentEnabled} = useTencentCaptcha()

    // The disclaimer refers to Google reCAPTCHA, so it is shown only when Google reCAPTCHA is
    // active for the site and Tencent Captcha is not enabled — both driven by site preferences.
    const showRecaptchaDisclaimer = !!appPreferences.apikeyv3_recaptcha && !isTencentEnabled

    return (
        <VStack align="flex-start" spacing="spacing.4" w="100%" {...containerProps}>
            <Box {...privacyMessageProps}>
                <FormattedMessage
                    id="label.contact.form.required.privacy.text"
                    defaultMessage={'For more information, see our {privacyLink}.'}
                    values={{
                        privacyLink: (
                            <Link
                                className="cc-privacy-link"
                                href={`/${locale.alias}/privacypolicy`}
                                target="_blank"
                                data-cs-override=""
                                rel="noreferrer"
                            >
                                {formatMessage({id: 'newsletter.registration.privacy.link', defaultMessage: 'Privacy Policy'})}
                            </Link>
                        ),
                    }}
                />
            </Box>

            {showRecaptchaDisclaimer && (
                <Box {...recaptchaMessageProps}>
                    <FormattedMessage
                        id="label.newsletter.form.required.recaptcha.text"
                        defaultMessage={'This site is protected by reCAPTCHA and the Google {privacyLink} and {termsLink} apply.'}
                        values={{
                            privacyLink: (
                                <Link
                                    className="cc-privacy-link"
                                    aria-label={formatMessage({
                                        id: 'newsletter.registration.privacy.arialabel',
                                        defaultMessage: 'Visit Google’s Privacy Policy',
                                    })}
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {formatMessage({id: 'newsletter.registration.privacy.link', defaultMessage: 'Privacy Policy'})}
                                </Link>
                            ),
                            termsLink: (
                                <Link
                                    className="cc-privacy-link"
                                    aria-label={formatMessage({
                                        id: 'newsletter.registration.service.arialabel',
                                        defaultMessage: 'Visit Google’s Terms of Service',
                                    })}
                                    href="https://policies.google.com/terms"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {formatMessage({id: 'newsletter.registration.service.link', defaultMessage: 'Terms of Service'})}
                                </Link>
                            ),
                        }}
                    />
                </Box>
            )}
        </VStack>
    )
}

FormPrivacyDisclaimer.displayName = 'FormPrivacyDisclaimer'

export {FormPrivacyDisclaimer}
